import { isEmpty } from 'lodash-es';
import React from 'react';

import { parseBidFloorKey } from '~/src/dash/apiV2/smartFloors/realtimeAdaptive';
import { formatTooltipDate, formatTooltipValue } from '~/src/dash/components/charts/common';
import { ValueType } from '~/src/dash/components/tables/common';
import {
  borderRadius,
  Col,
  Cols,
  fontSizing,
  Row,
  Rows,
  spacing,
  Span,
  Text,
  usePublicaTranslation,
} from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';
import { IStackedChartData } from '~/src/dash/views/channels/SmartFloors/SmartFloorsLiveAdaptiveView/config';

interface ISmartFloorTooltipProps {
  active?: boolean;
  isChartAggregatedByBidFloorType: boolean;
  currencySymbol: string;
  label?: string; // This is the X-axis value from Recharts
  labelMetric?: string; // This is our custom metric label
  language: string;
  payload?: Array<{
    dataKey: string;
    value: number;
    color: string;
    payload: IStackedChartData;
  }>;
  selectedBidFloorKeys: string[];
  valueType?: ValueType;
}

interface ITooltipDatum {
  bidFloor: number;
  color: string;
  isControlBidFloor: boolean;
  key: string;
  label: string;
  value: string | number;
}

export const SmartFloorTooltip = ({
  active,
  currencySymbol,
  isChartAggregatedByBidFloorType = false,
  label,
  labelMetric = '',
  language = 'en',
  payload,
  selectedBidFloorKeys,
  valueType = 'name',
}: ISmartFloorTooltipProps): React.ReactElement | null => {
  const { t } = usePublicaTranslation();
  if (!active || !payload || payload.length === 0) return null;

  // parse tootlip date
  const rawDate = payload[0].payload?.date || label;
  const date = typeof rawDate === 'string' && rawDate ? rawDate : '';
  const tooltipDate =
    formatTooltipDate(date, 0, language) || t('ErrorInvalidItem', { item: 'Date' });

  // calculate tooltip data
  const tooltipData: ITooltipDatum[] = payload
    .filter((item) => isEmpty(selectedBidFloorKeys) || selectedBidFloorKeys.includes(item.dataKey))
    .map((item, index: number): ITooltipDatum => {
      const { dataKey, value, color } = item;

      const isControlBidFloor = dataKey === item.payload?.controlBidFloorKey;
      const valueFormatted = formatTooltipValue(value, valueType, currencySymbol);
      const weightedAvgBidFloor =
        dataKey === item.payload?.controlBidFloorKey
          ? item.payload?.weightedAvgControlBidFloor
          : item.payload?.weightedAvgSmartBidFloor;

      return {
        bidFloor: isChartAggregatedByBidFloorType ? weightedAvgBidFloor : value,
        color,
        isControlBidFloor,
        key: `${dataKey}-${index}`,
        label: isChartAggregatedByBidFloorType
          ? `${dataKey}`
          : `${currencySymbol} ${parseBidFloorKey(dataKey).bidFloor}`,
        value: valueFormatted || 'N/A',
      };
    });

  return (
    <Rows
      style={{
        backgroundColor: publicaColors.white,
        border: `1px solid ${publicaColors.lightGray}`,
        padding: spacing.small,
        borderRadius: borderRadius.base,
      }}
    >
      <Row
        noPadding
        style={{
          borderBottom: `1px solid ${publicaColors.lightGray}`,
          marginBottom: spacing.small,
        }}
      >
        <Text extraSmall bold muted>
          {labelMetric}
        </Text>
        <Text small bold>
          {tooltipDate}
        </Text>
      </Row>

      {tooltipData.map((datum: ITooltipDatum) => {
        return (
          <Row key={datum.key} noPadding style={{ marginBottom: '2px' }}>
            <Cols
              gap={spacing.medium}
              style={{
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Col>
                <Cols gap={spacing.extraSmall} style={{ alignItems: 'center' }}>
                  <Col paddingBottom='1px'>
                    <Text color={datum.color} style={{ fontSize: '12px' }}>
                      {/* eslint-disable i18next/no-literal-string */}⬤
                      {/* eslint-enable i18next/no-literal-string */}
                    </Text>
                  </Col>
                  <Col width={isChartAggregatedByBidFloorType ? '4rem' : '6rem'}>
                    <Text small bold>
                      {datum.label}
                      {!isChartAggregatedByBidFloorType && datum.isControlBidFloor && (
                        <Span bold muted style={{ fontSize: fontSizing.extraSmall }}>
                          {' '}
                          {t('ControlWithParentheses')}
                        </Span>
                      )}
                    </Text>
                  </Col>
                  {isChartAggregatedByBidFloorType && (
                    <Col width='3rem'>
                      <Text small bold muted>
                        ${datum.bidFloor}
                      </Text>
                    </Col>
                  )}
                </Cols>
              </Col>
              <Col>
                <Text small>{datum.value}</Text>
              </Col>
            </Cols>
          </Row>
        );
      })}
    </Rows>
  );
};
